import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { useProjectService, useWorkspaceService } from '@web/app/features/home/hooks';
import { getProjectBadge } from '@web/app/shared';
import { useSelectedStore } from '@web/app/shared/stores';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

import { Button, Skeleton } from '@codes/ui-kit';

interface ProjectSwitcherProps {
    onOpenCreateProject: () => void;
}

export const ProjectSwitcher = ({ onOpenCreateProject }: ProjectSwitcherProps) => {
    const { project: myProject, setProject } = useSelectedStore();
    const { workspaceFromServer } = useWorkspaceService();
    const { myProjectsQueryResult } = useProjectService();
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!myProject) {
        return null;
    }

    if (myProjectsQueryResult.isFetching || myProjectsQueryResult.isRefetching) {
        return <Skeleton className="h-6 w-36" />;
    }

    if (myProjectsQueryResult.error) {
        return (
            <div className="flex gap-x-2 items-center">
                <div className="text-error">{t('project.error.fetchList')}</div>
                <Button
                    className="text-sm p-2 h-auto"
                    onClick={() => myProjectsQueryResult.refetch()}
                    disabled={myProjectsQueryResult.isRefetching}
                >
                    {t('project.error.retry')}
                </Button>
            </div>
        );
    }

    return (
        <>
            <span className="text-text-400 mx-2 max-md:mx-0 text-sm">/</span>
            <DropdownMenuTrigger asChild className="max-sm:max-w-[180px]" disabled={myProjectsQueryResult.isFetching}>
                <button className="flex items-center gap-1.5 py-1 px-2 !rounded-md hover:bg-hover transition-colors duration-150 focus:outline-none">
                    <span className="text-sm font-medium truncate">{myProject.name}</span>
                    <div className="max-xs:hidden">{getProjectBadge(myProject.stereo)}</div>
                    <ChevronsUpDown className="shrink-0 w-3.5 h-3.5 text-text-500" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[240px] p-1.5 rounded-lg space-y-0.5">
                <div className="max-h-48 overflow-y-auto">
                    {myProjectsQueryResult.data?.data?.map(project => (
                        <DropdownMenuItem
                            key={project.id}
                            onClick={() => {
                                setProject(project);
                                navigate(`/w/${workspaceFromServer?.id}/p/${project.id}`);
                            }}
                            className="cursor-pointer !rounded-md py-1.5 px-2 text-xs gap-1.5 hover:bg-hover truncate"
                        >
                            <div className="w-4 h-4 flex items-center justify-center">
                                {project.id === myProject.id && <Check className="w-4 h-4 text-text-800" />}
                            </div>
                            <div className="flex-1 flex gap-x-2 items-center justify-between min-w-0">
                                <span className="truncate flex-1">{project.name}</span>
                                {getProjectBadge(project.stereo, false, 'h-[18px] px-2 text-xs')}
                            </div>
                        </DropdownMenuItem>
                    ))}
                </div>

                <DropdownMenuSeparator className="m-0" />
                <DropdownMenuItem
                    className="cursor-pointer !rounded-md py-1.5 px-2 text-xs text-text-500 hover:bg-hover"
                    onClick={() => {
                        setProject(null);
                        if (workspaceFromServer) {
                            navigate(`/w/${workspaceFromServer.id}`, { replace: true });
                        } else {
                            navigate(`/w`, { replace: true });
                        }
                    }}
                >
                    {t('project.allProject')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="m-0" />
                <DropdownMenuItem
                    onClick={() => onOpenCreateProject()}
                    className="cursor-pointer !rounded-md py-1.5 px-2 text-xs font-normal text-text-500 hover:bg-hover"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    <span>{t('project.create.createProject')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </>
    );
};
